import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  HiPlay,
  HiStop,
  HiPlus,
  HiX,
  HiEye,
  HiClock,
  HiTrash,
  HiChevronDown,
  HiChevronUp,
  HiRefresh,
  HiSearch,
  HiStatusOnline,
  HiLightningBolt,
} from 'react-icons/hi';

const API_BASE = 'https://api.hedger.bar/api';
const getToken = () => localStorage.getItem('authToken');

const fetchApi = async (endpoint, options = {}) => {
  const url = `${API_BASE}${endpoint}`;
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };

  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(url, { ...options, headers });
  if (response.status === 204) return null;

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Request failed');

  return data;
};

const getAllStrategies = () => fetchApi('/strategies');
const getExchangeAccounts = () => fetchApi('/exchange-accounts');
const getLinkedStrategies = () => fetchApi('/strategies/linked');
const linkAccountToStrategy = (publicStrategyId, publicAccountId) =>
  fetchApi('/strategies/link-account', {
    method: 'POST',
    body: JSON.stringify({ publicStrategyId, publicAccountId }),
  });
const updateStrategyStatus = (publicStrategyId, status) =>
  fetchApi(`/strategies/${publicStrategyId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ autoTradeStatus: status }),
  });
const unlinkStrategyFromAccount = (publicStrategyId) =>
  fetchApi(`/strategies/${publicStrategyId}/unlink-account`, { method: 'PATCH' });

const getStrategyName = (strategy) => strategy.customStrategyName || strategy.strategyName || '-';

const getStrategyMeta = (strategy) => {
  const p = strategy?.parameters || {};
  const symbolA = p.symbol_a || p.symbolA || '';
  const symbolB = p.symbol_b || p.symbolB || '';
  const timeframe = p.timeframe;

  return {
    pairLabel: symbolA && symbolB ? `${symbolA} / ${symbolB}` : null,
    timeframeLabel: timeframe ? `${timeframe}m` : null,
  };
};

const getStatusMeta = (status, t) => {
  if (status === 'Enabled') {
    return {
      label: t('autotrading.status.active'),
      dot: 'bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.95)]',
      badge: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
    };
  }

  if (status === 'DisabledWithClose') {
    return {
      label: t('autotrading.status.stopping'),
      dot: 'bg-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.95)]',
      badge: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
    };
  }

  return {
    label: t('autotrading.status.disabled'),
    dot: 'bg-slate-400',
    badge: 'border-slate-600/80 bg-slate-800/70 text-slate-300',
  };
};

const metricAccentStyles = {
  blue: {
    icon: 'border-blue-500/35 bg-blue-500/15 text-blue-200',
    glow: 'bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.2),_transparent_62%)]',
  },
  emerald: {
    icon: 'border-emerald-500/35 bg-emerald-500/15 text-emerald-200',
    glow: 'bg-[radial-gradient(circle_at_top_right,_rgba(16,185,129,0.2),_transparent_62%)]',
  },
  amber: {
    icon: 'border-amber-500/35 bg-amber-500/15 text-amber-200',
    glow: 'bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.2),_transparent_62%)]',
  },
  cyan: {
    icon: 'border-cyan-500/35 bg-cyan-500/15 text-cyan-200',
    glow: 'bg-[radial-gradient(circle_at_top_right,_rgba(34,211,238,0.2),_transparent_62%)]',
  },
};

const CustomAccountSelect = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  const disabled = options.length === 0;

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition-all ${
          isOpen
            ? 'border-blue-400/60 bg-slate-900/85 text-white'
            : 'border-slate-700/80 bg-slate-950/45 text-slate-200'
        } ${disabled ? 'cursor-not-allowed opacity-65' : 'hover:border-blue-400/50'}`}
      >
        <span className={`block truncate ${selectedOption ? 'text-white' : 'text-slate-400'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          <HiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {isOpen && (
        <ul className="absolute z-[120] mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-slate-700/80 bg-slate-900/95 p-1 shadow-2xl shadow-black/40 backdrop-blur-md custom-scrollbar">
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={`w-full rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                  opt.value === value
                    ? 'bg-blue-500/25 text-blue-100'
                    : 'text-slate-200 hover:bg-slate-800/70 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MetricCard = ({ icon: Icon, title, value, accent = 'blue' }) => {
  const accentMeta = metricAccentStyles[accent] || metricAccentStyles.blue;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-800/90 via-slate-800/75 to-slate-900/85 p-4 shadow-xl shadow-black/20">
      <div className={`pointer-events-none absolute inset-0 ${accentMeta.glow}`} />
      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{title}</div>
          <div className="mt-2 text-2xl font-black text-slate-100">{value}</div>
        </div>
        <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${accentMeta.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, isLoading }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-700/70 bg-slate-900/95 shadow-2xl shadow-black/45">
        <div className="p-6">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">{message}</p>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-700/70 bg-slate-950/60 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-xl border border-slate-700/70 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-slate-800 disabled:opacity-60"
          >
            {t('autotrading.modal.btn_cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isLoading && (
              <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
            {isLoading ? t('autotrading.controls.processing') : t('autotrading.modal.btn_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ParamRow = ({ label, value, suffix = '' }) => {
  const safeValue = value !== undefined && value !== null && value !== '' ? `${value}${suffix}` : '-';

  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-700/60 bg-slate-900/55 px-3 py-2.5">
      <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
      <span className="ml-2 rounded-md bg-slate-800/90 px-2 py-1 font-mono text-xs text-slate-200">{safeValue}</span>
    </div>
  );
};

const StrategyDetailsModal = ({ strategy, onClose }) => {
  const { t } = useTranslation();

  const p = useMemo(() => {
    if (!strategy?.parameters) return {};

    const params = strategy.parameters;
    return {
      symbol_a: params.symbol_a,
      symbol_b: params.symbol_b,
      timeframe: params.timeframe,
      zscore_period: params.zscore_period,
      bar: params.bar,
      entry_amount: params.total_usdt_one_entry,
      commission: params.commission_rate,
      short_levels: Array.isArray(params.level_entry_negative)
        ? params.level_entry_negative.join(', ')
        : '',
      long_levels: Array.isArray(params.level_entry_positive)
        ? params.level_entry_positive.join(', ')
        : '',
    };
  }, [strategy]);

  if (!strategy) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-700/70 bg-slate-900/95 shadow-2xl shadow-black/45"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-700/70 bg-slate-950/60 p-5">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white">{t('autotrading.modal.params_title')}</h3>
            <p className="mt-1 truncate text-sm text-blue-200">{getStrategyName(strategy)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-700/80 p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
          >
            <HiX className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
          <ParamRow label={t('autotrading.params.symbol_a')} value={p.symbol_a} />
          <ParamRow label={t('autotrading.params.symbol_b')} value={p.symbol_b} />
          <ParamRow label={t('autotrading.params.timeframe')} value={p.timeframe} suffix="m" />
          <ParamRow label={t('autotrading.params.zscore')} value={p.zscore_period} />
          <ParamRow label={t('autotrading.params.bar')} value={p.bar} />
          <ParamRow label={t('autotrading.params.amount')} value={p.entry_amount} suffix="$" />
          <ParamRow label={t('autotrading.params.commission')} value={p.commission} suffix="%" />
          <ParamRow label={t('autotrading.params.short')} value={p.short_levels} />
          <div className="sm:col-span-2">
            <ParamRow label={t('autotrading.params.long')} value={p.long_levels} />
          </div>
        </div>
      </div>
    </div>
  );
};

const CreatePlanForm = ({ accounts, allStrategies, linkedStrategyIds, onLink, isLinking }) => {
  const { t } = useTranslation();
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedStrategyIds, setSelectedStrategyIds] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const availableStrategies = useMemo(
    () => allStrategies.filter((strategy) => !linkedStrategyIds.includes(strategy.publicStrategyId)),
    [allStrategies, linkedStrategyIds]
  );

  const filteredStrategies = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return availableStrategies;

    return availableStrategies.filter((strategy) => {
      const { pairLabel } = getStrategyMeta(strategy);
      const name = getStrategyName(strategy).toLowerCase();
      return name.includes(query) || (pairLabel || '').toLowerCase().includes(query);
    });
  }, [availableStrategies, searchValue]);

  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({
        value: account.publicAccountId,
        label: `${account.accountName} (${account.exchange})`,
      })),
    [accounts]
  );

  const selectedStrategies = useMemo(
    () => availableStrategies.filter((strategy) => selectedStrategyIds.includes(strategy.publicStrategyId)),
    [availableStrategies, selectedStrategyIds]
  );

  const handleStrategyToggle = (strategyId) => {
    setSelectedStrategyIds((prev) =>
      prev.includes(strategyId) ? prev.filter((id) => id !== strategyId) : [...prev, strategyId]
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedAccountId || selectedStrategyIds.length === 0) return;

    onLink(selectedAccountId, selectedStrategyIds);
    setSelectedStrategyIds([]);
  };

  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-700/60 bg-gradient-to-br from-slate-900/90 via-slate-900/82 to-slate-800/72 p-5 shadow-2xl sm:p-6">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.14),_transparent_58%)]" />
      <div className="pointer-events-none absolute -bottom-24 -left-12 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="text-2xl font-bold text-white">{t('autotrading.connect_title')}</h2>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <span className="rounded-full border border-slate-600/70 bg-slate-800/80 px-3 py-1">
              {t('accounts.your_accounts')}: {accounts.length}
            </span>
            <span className="rounded-full border border-slate-600/70 bg-slate-800/80 px-3 py-1">
              {t('dashboard.recent_table.all_strategies')}: {availableStrategies.length}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.95fr_1.65fr]">
            <div className="rounded-2xl border border-slate-700/70 bg-slate-800/70 p-4 shadow-inner shadow-slate-950/35">
              <div className="mb-3 flex items-center justify-between gap-2">
                <label className="text-sm font-semibold text-slate-200">{t('autotrading.select_account')}</label>
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-slate-500/70 bg-slate-800/85 px-2 text-[11px] font-bold text-slate-200">
                  1
                </span>
              </div>

              <CustomAccountSelect
                options={accountOptions}
                value={selectedAccountId}
                onChange={setSelectedAccountId}
                placeholder={t('autotrading.placeholder_account')}
              />
            </div>

            <div className="rounded-2xl border border-slate-700/70 bg-slate-800/70 p-4 shadow-inner shadow-slate-950/35">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <label className="text-sm font-semibold text-slate-200">{t('autotrading.select_strategies')}</label>
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-slate-500/70 bg-slate-800/85 px-2 text-[11px] font-bold text-slate-200">
                  2
                </span>
              </div>

              <div className="relative mb-3">
                <HiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder={`${t('analysis.search')}...`}
                  className="w-full rounded-xl border border-slate-700/80 bg-slate-950/45 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-400/60 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <div className="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto rounded-2xl border border-slate-700/70 bg-slate-800/45 p-2 custom-scrollbar sm:grid-cols-2">
                {filteredStrategies.length > 0 ? (
                  filteredStrategies.map((strategy) => {
                    const checked = selectedStrategyIds.includes(strategy.publicStrategyId);
                    const { pairLabel, timeframeLabel } = getStrategyMeta(strategy);

                    return (
                      <button
                        key={strategy.publicStrategyId}
                        type="button"
                        onClick={() => handleStrategyToggle(strategy.publicStrategyId)}
                        className={`rounded-xl border p-3 text-left transition-all ${
                          checked
                            ? 'border-blue-400/70 bg-gradient-to-br from-slate-700/80 via-blue-900/35 to-slate-800/90 shadow-lg shadow-blue-950/35 ring-1 ring-inset ring-blue-300/20'
                            : 'border-slate-600/75 bg-slate-900/65 hover:border-blue-400/35 hover:bg-slate-800/85'
                        }`}
                      >
                        <div className="text-sm font-semibold text-white">{getStrategyName(strategy)}</div>
                        {(pairLabel || timeframeLabel) && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-300">
                            {pairLabel && (
                              <span className="rounded-full border border-slate-600/70 bg-slate-900/70 px-2 py-0.5">
                                {pairLabel}
                              </span>
                            )}
                            {timeframeLabel && (
                              <span className="rounded-full border border-slate-600/70 bg-slate-900/70 px-2 py-0.5">
                                {timeframeLabel}
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="col-span-full rounded-xl border border-dashed border-slate-700/80 bg-slate-800/55 p-4 text-center text-sm text-slate-400">
                    {t('autotrading.no_strategies')}
                  </div>
                )}
              </div>
            </div>
          </div>
          {selectedStrategies.length > 0 && (
            <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-700/70 bg-slate-800/70 p-3">
              {selectedStrategies.map((strategy) => (
                <span
                  key={strategy.publicStrategyId}
                  className="inline-flex items-center gap-1.5 rounded-full border border-blue-400/35 bg-blue-500/15 px-2.5 py-1 text-xs text-blue-100"
                >
                  {getStrategyName(strategy)}
                  <button
                    type="button"
                    onClick={() => handleStrategyToggle(strategy.publicStrategyId)}
                    className="rounded-full p-0.5 text-blue-200/70 transition-colors hover:bg-blue-500/25 hover:text-white"
                  >
                    <HiX className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <button
            type="submit"
            disabled={isLinking || !selectedAccountId || selectedStrategyIds.length === 0}
            className={`inline-flex w-full items-center justify-center rounded-2xl border px-4 py-3.5 text-sm font-bold text-white transition-all ${
              isLinking
                ? 'cursor-not-allowed border-blue-500/35 bg-blue-700/80 opacity-80'
                : 'border-blue-500/35 bg-blue-600 hover:bg-blue-500 disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-800/70 disabled:text-slate-400'
            }`}
          >
            {isLinking ? (
              t('autotrading.btn_connecting')
            ) : (
              <>
                <HiPlus className="mr-2 h-5 w-5" />
                {t('autotrading.btn_connect')}
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

const StrategyStatusControl = ({ strategy, onStatusChange }) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);

  const runAction = async (nextStatus) => {
    setIsProcessing(true);
    try {
      await onStatusChange(strategy.publicStrategyId, nextStatus);
    } finally {
      setIsProcessing(false);
    }
  };

  if (strategy.autoTradeStatus === 'Disabled') {
    return (
      <button
        type="button"
        disabled={isProcessing}
        onClick={() => runAction('Enabled')}
        className="inline-flex items-center rounded-xl border border-emerald-500/35 bg-emerald-600/90 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isProcessing ? (
          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : (
          <HiPlay className="mr-1 h-4 w-4" />
        )}
        {t('autotrading.controls.start')}
      </button>
    );
  }

  if (strategy.autoTradeStatus === 'DisabledWithClose') {
    return (
      <button
        type="button"
        disabled={isProcessing}
        onClick={() => runAction('Disabled')}
        className="inline-flex items-center rounded-xl border border-rose-500/35 bg-rose-600/90 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isProcessing ? (
          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : (
          <HiStop className="mr-1 h-4 w-4" />
        )}
        {t('autotrading.controls.force_stop')}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        disabled={isProcessing}
        onClick={() => runAction('DisabledWithClose')}
        className="inline-flex items-center rounded-xl border border-slate-600/80 bg-slate-800/80 px-3 py-2 text-xs font-semibold text-amber-200 transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-70"
      >
        <HiClock className="mr-1 h-4 w-4" />
        {t('autotrading.controls.stop_graceful')}
      </button>
      <button
        type="button"
        disabled={isProcessing}
        onClick={() => runAction('Disabled')}
        className="inline-flex items-center rounded-xl border border-rose-500/35 bg-rose-600/90 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-70"
      >
        <HiStop className="mr-1 h-4 w-4" />
        {t('autotrading.controls.stop')}
      </button>
    </div>
  );
};

const StrategyCard = ({ strategy, onStatusChange, onViewDetails, onRequestUnlink }) => {
  const { t } = useTranslation();
  const statusMeta = getStatusMeta(strategy.autoTradeStatus, t);
  const { pairLabel, timeframeLabel } = getStrategyMeta(strategy);

  return (
    <article className="rounded-2xl border border-slate-600/80 bg-gradient-to-br from-slate-900/92 via-slate-900/88 to-slate-800/90 p-4 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.95)] ring-1 ring-inset ring-slate-300/10 transition-all hover:border-slate-500/90 hover:ring-slate-200/15">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-base font-bold text-white">{getStrategyName(strategy)}</h4>
            <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusMeta.badge}`}>
              <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
              {statusMeta.label}
            </span>
          </div>

          {(pairLabel || timeframeLabel) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-300">
              {pairLabel && (
                <span className="rounded-full border border-slate-600/70 bg-slate-800/75 px-2 py-0.5">{pairLabel}</span>
              )}
              {timeframeLabel && (
                <span className="rounded-full border border-slate-600/70 bg-slate-800/75 px-2 py-0.5">{timeframeLabel}</span>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onViewDetails(strategy)}
              className="inline-flex items-center rounded-lg border border-blue-500/30 bg-blue-500/10 px-2.5 py-1.5 text-xs font-semibold text-blue-200 transition-colors hover:bg-blue-500/20"
            >
              <HiEye className="mr-1 h-4 w-4" />
              {t('autotrading.modal.settings')}
            </button>
            <button
              type="button"
              onClick={() => onRequestUnlink(strategy)}
              className="inline-flex items-center rounded-lg border border-rose-500/35 bg-rose-500/10 px-2.5 py-1.5 text-xs font-semibold text-rose-200 transition-colors hover:bg-rose-500/20"
            >
              <HiTrash className="mr-1 h-4 w-4" />
              {t('autotrading.modal.unlink')}
            </button>
          </div>
        </div>

        <div className="w-full lg:w-auto">
          <StrategyStatusControl strategy={strategy} onStatusChange={onStatusChange} />
        </div>
      </div>
    </article>
  );
};

const AccountGroupAccordion = ({
  accountInfo,
  strategies,
  isExpanded,
  onToggle,
  onStatusChange,
  onViewDetails,
  onRequestUnlink,
}) => {
  const counts = useMemo(
    () => ({
      enabled: strategies.filter((item) => item.autoTradeStatus === 'Enabled').length,
      stopping: strategies.filter((item) => item.autoTradeStatus === 'DisabledWithClose').length,
      disabled: strategies.filter((item) => item.autoTradeStatus !== 'Enabled' && item.autoTradeStatus !== 'DisabledWithClose').length,
    }),
    [strategies]
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-700/70 bg-gradient-to-br from-slate-800/85 via-slate-800/72 to-slate-900/80 shadow-xl shadow-black/10">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 border-b border-slate-700/70 bg-slate-900/65 px-4 py-4 text-left transition-colors hover:bg-slate-800/80"
      >
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-white">{accountInfo.accountName}</h3>
          <div className="mt-1 inline-flex items-center rounded-full border border-slate-600/70 bg-slate-800/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-300">
            {accountInfo.exchange}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="rounded-full border border-emerald-500/35 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-200">
            {counts.enabled}
          </span>
          <span className="rounded-full border border-amber-500/35 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-200">
            {counts.stopping}
          </span>
          <span className="rounded-full border border-slate-600/75 bg-slate-800/85 px-2.5 py-1 text-xs font-semibold text-slate-300">
            {counts.disabled}
          </span>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700/80 bg-slate-800/85 text-slate-300">
            {isExpanded ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="grid grid-cols-1 gap-4 p-4">
          {strategies.map((strategy) => (
            <StrategyCard
              key={strategy.publicStrategyId}
              strategy={strategy}
              onStatusChange={onStatusChange}
              onViewDetails={onViewDetails}
              onRequestUnlink={onRequestUnlink}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const AutoTradingPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [linkedStrategies, setLinkedStrategies] = useState([]);
  const [allStrategies, setAllStrategies] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [expandedAccounts, setExpandedAccounts] = useState({});

  const [modalStrategy, setModalStrategy] = useState(null);
  const [unlinkModal, setUnlinkModal] = useState({
    isOpen: false,
    strategyId: null,
    strategyName: '',
    isLoading: false,
  });

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!user?.login) {
      setIsLoading(false);
      return;
    }

    if (!silent) setIsLoading(true);

    try {
      const [linkedData, allData, accountsData] = await Promise.all([
        getLinkedStrategies(),
        getAllStrategies(),
        getExchangeAccounts(),
      ]);

      setLinkedStrategies(Array.isArray(linkedData) ? linkedData : []);
      setAllStrategies(Array.isArray(allData) ? allData : []);
      setAccounts(Array.isArray(accountsData?.accounts) ? accountsData.accounts : []);
    } catch (error) {
      console.error('Failed to load auto-trading data:', error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [user?.login]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const groupedStrategies = useMemo(() => {
    const groups = new Map();

    linkedStrategies.forEach((strategy) => {
      const account = strategy.account;
      if (!account?.publicAccountId) return;

      const accountId = account.publicAccountId;
      if (!groups.has(accountId)) {
        groups.set(accountId, {
          accountInfo: account,
          strategies: [],
        });
      }

      groups.get(accountId).strategies.push(strategy);
    });

    return Array.from(groups.values()).sort((a, b) => {
      const enabledA = a.strategies.filter((item) => item.autoTradeStatus === 'Enabled').length;
      const enabledB = b.strategies.filter((item) => item.autoTradeStatus === 'Enabled').length;

      if (enabledB !== enabledA) return enabledB - enabledA;
      if (b.strategies.length !== a.strategies.length) return b.strategies.length - a.strategies.length;

      return String(a.accountInfo.accountName || '').localeCompare(String(b.accountInfo.accountName || ''));
    });
  }, [linkedStrategies]);

  useEffect(() => {
    setExpandedAccounts((prev) => {
      if (groupedStrategies.length === 0) return {};

      const next = {};
      groupedStrategies.forEach((group, index) => {
        const accountId = group.accountInfo.publicAccountId;
        next[accountId] = Object.prototype.hasOwnProperty.call(prev, accountId) ? prev[accountId] : index === 0;
      });

      return next;
    });
  }, [groupedStrategies]);

  const filteredGroups = groupedStrategies;

  const totalActive = useMemo(
    () => linkedStrategies.filter((strategy) => strategy.autoTradeStatus === 'Enabled').length,
    [linkedStrategies]
  );

  const totalStopping = useMemo(
    () => linkedStrategies.filter((strategy) => strategy.autoTradeStatus === 'DisabledWithClose').length,
    [linkedStrategies]
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadData({ silent: true });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLinkStrategies = async (accountId, strategyIds) => {
    setIsLinking(true);

    try {
      await Promise.all(strategyIds.map((strategyId) => linkAccountToStrategy(strategyId, accountId)));
      const linkedData = await getLinkedStrategies();
      setLinkedStrategies(Array.isArray(linkedData) ? linkedData : []);
    } catch (error) {
      console.error('Error connecting strategies:', error);
    } finally {
      setIsLinking(false);
    }
  };

  const handleStatusChange = async (strategyId, newStatus) => {
    try {
      const data = await updateStrategyStatus(strategyId, newStatus);
      const nextStatus = data?.strategy?.autoTradeStatus || newStatus;

      setLinkedStrategies((prev) =>
        prev.map((strategy) =>
          strategy.publicStrategyId === strategyId
            ? { ...strategy, autoTradeStatus: nextStatus }
            : strategy
        )
      );
    } catch (error) {
      console.error('Failed to update strategy status:', error);
    }
  };

  const handleRequestUnlink = (strategy) => {
    setUnlinkModal({
      isOpen: true,
      strategyId: strategy.publicStrategyId,
      strategyName: getStrategyName(strategy),
      isLoading: false,
    });
  };

  const confirmUnlink = async () => {
    if (!unlinkModal.strategyId) return;

    setUnlinkModal((prev) => ({ ...prev, isLoading: true }));

    try {
      await unlinkStrategyFromAccount(unlinkModal.strategyId);
      setLinkedStrategies((prev) =>
        prev.filter((strategy) => strategy.publicStrategyId !== unlinkModal.strategyId)
      );
      setUnlinkModal({ isOpen: false, strategyId: null, strategyName: '', isLoading: false });
    } catch (error) {
      console.error('Error unlinking strategy:', error);
      setUnlinkModal((prev) => ({ ...prev, isLoading: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-blue-400/40 border-t-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 animate-fadeIn">
      <StrategyDetailsModal strategy={modalStrategy} onClose={() => setModalStrategy(null)} />

      <ConfirmationModal
        isOpen={unlinkModal.isOpen}
        onClose={() => setUnlinkModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmUnlink}
        isLoading={unlinkModal.isLoading}
        title={t('autotrading.modal.unlink_title')}
        message={t('autotrading.modal.unlink_msg', { name: unlinkModal.strategyName })}
      />

      <section className="relative overflow-hidden rounded-3xl border border-slate-700/60 bg-gradient-to-br from-slate-900/90 via-slate-900/82 to-slate-800/72 p-5 shadow-2xl sm:p-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(37,99,235,0.18),_transparent_56%)]" />
        <div className="pointer-events-none absolute -top-20 right-12 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-emerald-500/8 blur-3xl" />
        <div className="relative">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-white">{t('autotrading.page_title')}</h1>
              <p className="mt-1 text-sm text-slate-400">{t('autotrading.connect_title')}</p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-200 transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <HiRefresh className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? t('dashboard.refreshing') : t('dashboard.refresh')}
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={HiStatusOnline}
              title={t('autotrading.connected_title')}
              value={linkedStrategies.length}
              accent="blue"
            />
            <MetricCard
              icon={HiLightningBolt}
              title={t('autotrading.status.active')}
              value={totalActive}
              accent="emerald"
            />
            <MetricCard
              icon={HiClock}
              title={t('autotrading.status.stopping')}
              value={totalStopping}
              accent="amber"
            />
            <MetricCard
              icon={HiStatusOnline}
              title={t('accounts.your_accounts')}
              value={accounts.length}
              accent="cyan"
            />
          </div>
        </div>
      </section>

      <CreatePlanForm
        accounts={accounts}
        allStrategies={allStrategies}
        linkedStrategyIds={linkedStrategies.map((strategy) => strategy.publicStrategyId)}
        onLink={handleLinkStrategies}
        isLinking={isLinking}
      />

      <section className="relative overflow-hidden space-y-4 rounded-3xl border border-slate-700/60 bg-gradient-to-br from-slate-900/90 via-slate-900/82 to-slate-800/72 p-5 shadow-2xl sm:p-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.12),_transparent_58%)]" />
        <div className="pointer-events-none absolute -right-16 bottom-0 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold text-white">{t('autotrading.connected_title')}</h2>
          <span className="rounded-full border border-slate-600/70 bg-slate-800/80 px-3 py-1.5 text-xs text-slate-300">
            {t('autotrading.active_count')}: {linkedStrategies.length}
          </span>
        </div>

        <div className="relative">
          {filteredGroups.length > 0 ? (
            <div className="space-y-4">
              {filteredGroups.map((group) => {
                const accountId = group.accountInfo.publicAccountId;

                return (
                  <AccountGroupAccordion
                    key={accountId}
                    accountInfo={group.accountInfo}
                    strategies={group.strategies}
                    isExpanded={!!expandedAccounts[accountId]}
                    onToggle={() =>
                      setExpandedAccounts((prev) => ({
                        ...prev,
                        [accountId]: !prev[accountId],
                      }))
                    }
                    onStatusChange={handleStatusChange}
                    onViewDetails={setModalStrategy}
                    onRequestUnlink={handleRequestUnlink}
                  />
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-700/80 bg-slate-800/60 py-14 text-center text-slate-400">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700/80 bg-slate-800/85">
                <HiPlus className="h-5 w-5" />
              </div>
              <p>{t('autotrading.empty_state')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AutoTradingPage;
